import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
} from 'recharts';
import type { ChartDataPoint, ChartSeries } from './types';
import { CHART_COLOR_PALETTE } from './types';

interface BarChartProps {
  data: ChartDataPoint[];
  series?: ChartSeries[];
  dataKey?: string;
  xAxisKey?: string;
  height?: number;
  showGrid?: boolean;
  showLegend?: boolean;
  stacked?: boolean;
  horizontal?: boolean;
  barSize?: number;
  colorByValue?: boolean;
  colors?: string[];
}

export default function BarChart({
  data,
  series,
  dataKey = 'value',
  xAxisKey = 'name',
  height = 280,
  showGrid = true,
  showLegend = false,
  stacked = false,
  horizontal = false,
  barSize = 20,
  colorByValue = false,
  colors,
}: BarChartProps) {
  // Use provided colors array for colorByValue, or fall back to CHART_COLOR_PALETTE
  const colorPalette = colors || CHART_COLOR_PALETTE;
  // If no series provided, create a default one
  const chartSeries = series || [
    { dataKey, name: dataKey, color: CHART_COLOR_PALETTE[0] },
  ];

  const ChartComponent = horizontal ? (
    <RechartsBarChart
      data={data}
      layout="vertical"
      margin={{ top: 10, right: 10, left: 60, bottom: 0 }}
    >
      {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />}
      <XAxis type="number" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
      <YAxis
        type="category"
        dataKey={xAxisKey}
        tick={{ fontSize: 11 }}
        tickLine={false}
        axisLine={{ stroke: '#e5e7eb' }}
        width={50}
      />
      <Tooltip
        contentStyle={{
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          fontSize: '12px',
        }}
      />
      {showLegend && <Legend />}
      {chartSeries.map((s, index) => (
        <Bar
          key={s.dataKey}
          dataKey={s.dataKey}
          name={s.name}
          fill={s.color || CHART_COLOR_PALETTE[index % CHART_COLOR_PALETTE.length]}
          stackId={stacked ? 'stack' : undefined}
          barSize={barSize}
          radius={[0, 4, 4, 0]}
        >
          {colorByValue &&
            data.map((_, i) => (
              <Cell
                key={`cell-${i}`}
                fill={colorPalette[i % colorPalette.length]}
              />
            ))}
        </Bar>
      ))}
    </RechartsBarChart>
  ) : (
    <RechartsBarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
      {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />}
      <XAxis
        dataKey={xAxisKey}
        tick={{ fontSize: 11 }}
        tickLine={false}
        axisLine={{ stroke: '#e5e7eb' }}
      />
      <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={40} />
      <Tooltip
        contentStyle={{
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          fontSize: '12px',
        }}
      />
      {showLegend && <Legend />}
      {chartSeries.map((s, index) => (
        <Bar
          key={s.dataKey}
          dataKey={s.dataKey}
          name={s.name}
          fill={s.color || CHART_COLOR_PALETTE[index % CHART_COLOR_PALETTE.length]}
          stackId={stacked ? 'stack' : undefined}
          barSize={barSize}
          radius={[4, 4, 0, 0]}
        >
          {colorByValue &&
            data.map((_, i) => (
              <Cell
                key={`cell-${i}`}
                fill={colorPalette[i % colorPalette.length]}
              />
            ))}
        </Bar>
      ))}
    </RechartsBarChart>
  );

  return (
    <ResponsiveContainer width="100%" height={height}>
      {ChartComponent}
    </ResponsiveContainer>
  );
}
